import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, X, UserPlus, Check } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

/**
 * AddMembersModal — Wave 19 #92.
 *
 * Picker that adds new staff users to an existing chat thread without
 * replacing the existing roster. Uses POST /chats/{tid}/members/add so
 * the operation is a strict additive set-union and is idempotent.
 */
export default function AddMembersModal({ thread, onClose, onAdded }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(() => new Set());

  const existing = useMemo(
    () => new Set(thread?.member_ids || []),
    [thread],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/users");
        if (!alive) return;
        // Strip any portal/client users defensively (server already does)
        setUsers((data || []).filter((u) => u.role !== "client"));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load users.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => !existing.has(u.id))
      .filter((u) => {
        if (!q) return true;
        return (
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.role || "").toLowerCase().includes(q)
        );
      });
  }, [users, query, existing]);

  const toggle = (id) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/chats/${thread.id}/members/add`, {
        member_ids: Array.from(picked),
      });
      const n = (data?.added || []).length;
      if (n === 0) {
        toast.message("Already in the chat.");
      } else {
        toast.success(`Added ${n} member${n === 1 ? "" : "s"}.`);
      }
      onAdded?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add members.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6 hv-safe-top hv-safe-bottom hv-safe-x"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="chat-add-members-modal"
    >
      <div
        className="w-full max-w-md bg-[#121419] border border-white/10 rounded-t-2xl md:rounded-lg overflow-hidden flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#1fb6c1]" />
            <h3 className="font-display text-base font-bold tracking-tight">
              Add to chat
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[#647087] hover:text-white transition p-1 rounded"
            data-testid="chat-add-members-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#647087] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, role…"
              className="hv-input pl-9 text-sm"
              data-testid="chat-add-members-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-[40vh]">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-xs text-[#a0aabf]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading staff…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-xs text-[#647087] py-10">
              {query
                ? "No matches."
                : "Everyone's already in this chat."}
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((u) => {
                const isPicked = picked.has(u.id);
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => toggle(u.id)}
                      data-testid={`chat-add-pick-${u.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition border text-left ${
                        isPicked
                          ? "bg-[#1fb6c1]/15 border-[#1fb6c1]/50"
                          : "bg-transparent border-transparent hover:bg-white/[0.03]"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                          isPicked
                            ? "bg-[#1fb6c1] border-[#1fb6c1]"
                            : "border-white/20"
                        }`}
                      >
                        {isPicked && <Check className="w-3 h-3 text-black" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white truncate">
                          {u.name || u.email || "—"}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
                          {u.role}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between gap-3">
          <div className="text-xs text-[#a0aabf]" data-testid="chat-add-members-count">
            {picked.size === 0
              ? "Pick people to add"
              : `${picked.size} selected`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="chat-add-members-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={picked.size === 0 || busy}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="chat-add-members-submit"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-3.5 h-3.5" /> Add
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
